/** Which till this browser is.
 *
 *  Receipts are routed by station id: the register sends it with a sale,
 *  and the server hands the job to the `pos-printer-service` that
 *  registered under the same id. Get it wrong and the sale still records
 *  perfectly — nothing prints, which is the failure mode this module's
 *  comments exist to make findable.
 *
 *  Where it comes from, in order:
 *
 *  1. `localStorage['pos.stationId']` — set per machine, survives a
 *     reload, and is what a second till would use.
 *  2. `REACT_APP_POS_STATION` — baked in at build time. One value for
 *     every till that loads this deploy, which is right while there is
 *     exactly one register.
 *  3. Empty — no station, so no printing. Deliberately not a guess: a
 *     made-up id would route receipts to a printer that doesn't exist
 *     and look identical to a printer that's switched off.
 *
 *  This is the one piece of per-machine state the register keeps, and
 *  it is a deliberate exception to POS_PLAN §10.1's "keep the till
 *  stateless" rule — printing is the first thing the register does that
 *  is genuinely about *this counter* rather than about the shop. One
 *  line of localStorage is the smallest form that exception can take.
 */

const KEY = 'pos.stationId';

/** The configured station id, or `''` when this till has none. */
export function stationId(): string {
  try {
    const stored = window.localStorage.getItem(KEY);
    if (stored && stored.trim() !== '') return stored.trim();
  } catch {
    // Private browsing, or storage disabled by policy. Fall through to
    // the build-time default rather than breaking the sale.
  }
  return (process.env.REACT_APP_POS_STATION ?? '').trim();
}

/** Point this machine at a different printer. Takes effect on the next
 *  sale — nothing caches it. */
export function setStationId(id: string): void {
  try {
    if (id.trim() === '') window.localStorage.removeItem(KEY);
    else window.localStorage.setItem(KEY, id.trim());
  } catch {
    // Nothing to do: without storage this till keeps the build-time
    // default for the rest of the session.
  }
}
